import React from 'react'
import {
  CollateralIdentifierType,
  CollateralType,
  ColumnMapping,
  CouponFrequency,
  EntityStatus,
  IndexLag,
  RateType,
  SpreadsheetMapping,
  TradingType
} from 'api'
import {
  CollateralIdentifierTypeHelper,
  CollateralTypeHelper,
  CouponFrequencyHelper,
  EntityStatusHelper,
  IndexLagHelper,
  RateTypeHelper,
  TradingTypeHelper
} from 'model'
import { CollateralSpreadsheetParseResult } from './parseCollateralSpreadsheet'
import { Locale, Translation } from 'locale'
import {
  ColumnMappingDatePanel,
  ColumnMappingEnumPanel,
  ColumnMappingStringPanel,
  ColumnsContainer,
  ColumnsHeader,
  ColumnsHeaderPanel
} from '../upload/mappingDialog'

export interface CollateralMappingDialogColumnsProps {
  mapping: SpreadsheetMapping
  parseResult: CollateralSpreadsheetParseResult
  onUpdate: (mapping: SpreadsheetMapping) => unknown
}

export const CollateralMappingDialogColumns: React.FC<CollateralMappingDialogColumnsProps> = props => {
  const { mapping, parseResult, onUpdate } = props

  const handleColumnUpdate = (columnMapping: ColumnMapping, fieldName: keyof SpreadsheetMapping) =>
    onUpdate({ ...mapping, [fieldName]: columnMapping })

  return (
    <ColumnsContainer>
      <ColumnsHeaderPanel>
        <ColumnsHeader>{Locale.translate(Translation.TC_COLLATERAL_FIELD)}</ColumnsHeader>
        <ColumnsHeader>{Locale.translate(Translation.TC_SPREADSHEET_COLUMN)}</ColumnsHeader>
      </ColumnsHeaderPanel>

      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_TRADING)}
        fieldName="tradingStatus"
        columnMapping={mapping.tradingStatus}
        parseResult={parseResult}
        Enum={EntityStatus}
        enumHelper={EntityStatusHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_PLATFORM)}
        fieldName="platform"
        columnMapping={mapping.platform}
        parseResult={parseResult}
        Enum={[CollateralIdentifierType.ISIN, CollateralIdentifierType.CUSIP]}
        enumHelper={CollateralIdentifierTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_SHORT_ID)}
        fieldName="shortId"
        columnMapping={mapping.shortId}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ISIN)}
        fieldName="isin"
        columnMapping={mapping.isin}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_FISN)}
        fieldName="fisn"
        columnMapping={mapping.fisn}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_CFI)}
        fieldName="cfi"
        columnMapping={mapping.cfi}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_CUSIP)}
        fieldName="cusip"
        columnMapping={mapping.cusip}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_CURRENCY)}
        fieldName="currency"
        columnMapping={mapping.currency}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_COUNTRY)}
        fieldName="country"
        columnMapping={mapping.country}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_SECTOR)}
        fieldName="sector"
        columnMapping={mapping.sector}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_TRADING_TYPE)}
        fieldName="tradingType"
        columnMapping={mapping.tradingType}
        parseResult={parseResult}
        Enum={TradingType}
        enumHelper={TradingTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_CLEAN_PRICE)}
        fieldName="cleanPrice"
        columnMapping={mapping.cleanPrice}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_TICKET_SIZE)}
        fieldName="ticketSize"
        columnMapping={mapping.ticketSize}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_ISSUER)}
        fieldName="issuer"
        columnMapping={mapping.issuer}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_FIRST_SETTLEMENT_DATE)}
        fieldName="firstSettlementDate"
        columnMapping={mapping.firstSettlementDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_ACCRUAL_DATE)}
        fieldName="accrualDate"
        columnMapping={mapping.accrualDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_FIRST_COUPON_DATE)}
        fieldName="firstCouponDate"
        columnMapping={mapping.firstCouponDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingDatePanel
        fieldTitle={Locale.translate(Translation.TC_MATURITY_DATE)}
        fieldName="maturityDate"
        columnMapping={mapping.maturityDate}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_COUPON_TYPE)}
        fieldName="couponType"
        columnMapping={mapping.couponType}
        parseResult={parseResult}
        Enum={RateType}
        enumHelper={RateTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_BASIS_CONVENTION)}
        fieldName="basisConvention"
        columnMapping={mapping.basisConvention}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_COUPON)}
        fieldName="coupon"
        columnMapping={mapping.coupon}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_FREQUENCY)}
        fieldName="frequency"
        columnMapping={mapping.frequency}
        parseResult={parseResult}
        Enum={CouponFrequency}
        enumHelper={CouponFrequencyHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_COLLATERAL_TYPE)}
        fieldName="collateralType"
        columnMapping={mapping.collateralType}
        parseResult={parseResult}
        Enum={CollateralType}
        enumHelper={CollateralTypeHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_INDEX)}
        fieldName="index"
        columnMapping={mapping.index}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingEnumPanel
        fieldTitle={Locale.translate(Translation.TC_INDEX_LAG)}
        fieldName="lag"
        columnMapping={mapping.lag}
        parseResult={parseResult}
        Enum={IndexLag}
        enumHelper={IndexLagHelper}
        onUpdate={handleColumnUpdate}
      />
      <ColumnMappingStringPanel
        fieldTitle={Locale.translate(Translation.TC_BASE_INDEX_VALUE)}
        fieldName="baseIndexValue"
        columnMapping={mapping.baseIndexValue}
        parseResult={parseResult}
        onUpdate={handleColumnUpdate}
      />
    </ColumnsContainer>
  )
}
